import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { JuniorLayout } from '../../components/layout/JuniorLayout';
import { ActivityShell } from '../../components/activity/ActivityShell';
import { RequireJuniorSession } from '../../components/auth/RequireJuniorSession';
import { ClingCantaLobby } from '../../components/clingCanta/ClingCantaLobby';
import { ClingCantaResults } from '../../components/clingCanta/ClingCantaResults';
import { ClingWorldStage, type ClingWorldStageHandle } from '../../components/clingCanta/ClingWorldStage';
import { useJuniorPitchDetection } from '../../hooks/useJuniorPitchDetection';
import {
  CLING_MELODY_EXERCISES,
  type ClingMelodyExercise,
  type ClingPlayMode,
  type SingPitchId,
} from '../../lib/melody/clingCantaConfig';
import {
  beginCountdown,
  beginDemo,
  beginPlay,
  createClingGameState,
  tickClingGame,
  type ClingGameState,
} from '../../lib/melody/clingCantaEngine';
import {
  beginExploreCountdown,
  beginExplorePlay,
  CLING_EXPLORE_LEVELS,
  createExploreState,
  tickExploreGame,
  type ClingExploreLevel,
  type ExploreGameState,
} from '../../lib/melody/clingExploreEngine';
import {
  applyNoteSelectionToLevel,
  loadClingNoteSelection,
  saveClingNoteSelection,
  togglePitchSelection,
  type ClingNoteSelection,
} from '../../lib/melody/clingNoteSelectionConfig';
import { resumeMelodyAudio } from '../../lib/melody/melodyNoteSounds';
import { playSingPitchSample, preloadClingNoteSamples } from '../../lib/melody/clingNoteSamples';
import { clingFlyFrame, CLING_SHEET, loadProcessedClingSheet } from '../../lib/melody/clingSprite';
import {
  loadVoiceTessitura,
  saveVoiceTessitura,
  type VoiceTessitura,
} from '../../lib/melody/clingVoiceConfig';
import {
  loadClingCharacter,
  saveClingCharacter,
  getClingCharacter,
  type ClingCharacterId,
} from '../../lib/melody/clingCharacterConfig';
import {
  loadClingVisualSettings,
  saveClingVisualSettings,
  type ClingVisualSettings,
} from '../../lib/melody/clingVisualConfig';
import { applyConfigSong, type ClingConfigSong } from '../../lib/melody/clingConfigSongs';
import { allSingPitchIds } from '../../lib/melody/clingCantaPitch';
import {
  PresentationModeProvider,
  usePresentationMode,
} from '../../contexts/PresentationModeContext';

type PagePhase = 'lobby' | 'round' | 'results';

function ClingCantaPageInner() {
  const { isPresentation, setPresentation } = usePresentationMode();
  const [playMode, setPlayMode] = useState<ClingPlayMode>('exploracion');
  const [exercise, setExercise] = useState<ClingMelodyExercise>(CLING_MELODY_EXERCISES[0]!);
  const [exploreLevel, setExploreLevel] = useState<ClingExploreLevel>(CLING_EXPLORE_LEVELS[0]!);
  const [pagePhase, setPagePhase] = useState<PagePhase>('lobby');
  const [voiceTessitura, setVoiceTessitura] = useState<VoiceTessitura>(() => loadVoiceTessitura());
  const [characterId, setCharacterId] = useState<ClingCharacterId>(() => loadClingCharacter());
  const [noteSelection, setNoteSelection] = useState<ClingNoteSelection>(() => loadClingNoteSelection());
  const [visualSettings, setVisualSettings] = useState<ClingVisualSettings>(() =>
    loadClingVisualSettings()
  );
  const [cantoGame, setCantoGame] = useState<ClingGameState>(() =>
    createClingGameState(CLING_MELODY_EXERCISES[0]!)
  );
  const [exploreGame, setExploreGame] = useState<ExploreGameState>(() =>
    createExploreState(CLING_EXPLORE_LEVELS[0]!)
  );
  const [animCycle, setAnimCycle] = useState(0);

  const cantoRef = useRef(cantoGame);
  cantoRef.current = cantoGame;
  const exploreRef = useRef(exploreGame);
  exploreRef.current = exploreGame;

  const stageSizeRef = useRef({ w: 640, h: 360 });
  const lastTsRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const stageRef = useRef<ClingWorldStageHandle>(null);
  const animAccumRef = useRef(0);

  const configuredExploreLevel = useMemo(
    () => applyNoteSelectionToLevel(exploreLevel, noteSelection),
    [exploreLevel, noteSelection]
  );

  const gamePitchLanes = useMemo(
    () => (playMode === 'exploracion' ? configuredExploreLevel.activeLanes : exercise.lanes),
    [playMode, configuredExploreLevel.activeLanes, exercise.lanes]
  );

  /** Detección sobre todo el rango vocal; los carriles del nivel solo afectan al teclado simulado. */
  const pitch = useJuniorPitchDetection(allSingPitchIds(), voiceTessitura);
  const pitchRef = useRef(pitch);
  pitchRef.current = pitch;
  const pitchApiRef = useRef(pitch);
  pitchApiRef.current = pitch;

  const subtitle =
    playMode === 'exploracion' ? configuredExploreLevel.title : exercise.title;

  const handleSizeChange = useCallback((w: number, h: number) => {
    stageSizeRef.current = { w, h };
  }, []);

  const stopGameLoop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const stopRound = useCallback(() => {
    stopGameLoop();
  }, [stopGameLoop]);

  const startRound = useCallback(() => {
    lastTsRef.current = 0;
    animAccumRef.current = 0;
    void resumeMelodyAudio();

    if (playMode === 'exploracion') {
      const { w, h } = stageSizeRef.current;
      const next = createExploreState(configuredExploreLevel, w, h);
      preloadClingNoteSamples(configuredExploreLevel.displayLanes);
      preloadClingNoteSamples(configuredExploreLevel.activeLanes);
      setExploreGame(next);
      exploreRef.current = next;
    } else {
      const next = beginDemo(createClingGameState(exercise), stageSizeRef.current.w);
      setCantoGame(next);
      cantoRef.current = next;
    }
    pitchApiRef.current.startDetection();
    setPagePhase('round');
  }, [configuredExploreLevel, exercise, playMode]);

  const handleRetry = useCallback(() => {
    stopRound();
    startRound();
  }, [startRound, stopRound]);

  const handleLobby = useCallback(() => {
    stopRound();
    setPagePhase('lobby');
    setCantoGame(createClingGameState(exercise));
    setExploreGame(createExploreState(configuredExploreLevel));
  }, [exercise, configuredExploreLevel, stopRound]);

  const handleVoiceTessitura = useCallback((voice: VoiceTessitura) => {
    setVoiceTessitura(voice);
    saveVoiceTessitura(voice);
  }, []);

  const handleCharacter = useCallback((id: ClingCharacterId) => {
    setCharacterId(id);
    saveClingCharacter(id);
  }, []);

  const handleVisual = useCallback((visual: ClingVisualSettings) => {
    setVisualSettings(visual);
    saveClingVisualSettings(visual);
  }, []);

  const handleNoteToggle = useCallback((pitchId: SingPitchId) => {
    setNoteSelection((prev) => {
      const next = togglePitchSelection(prev, pitchId);
      saveClingNoteSelection(next);
      return next;
    });
  }, []);

  const handleSelectSong = useCallback((song: ClingConfigSong) => {
    const applied = applyConfigSong(song);
    setPlayMode(applied.mode);
    if (applied.exercise) setExercise(applied.exercise);
    if (applied.exploreLevel) setExploreLevel(applied.exploreLevel);
  }, []);

  useEffect(() => {
    void loadProcessedClingSheet();
  }, []);

  useEffect(() => {
    setPresentation(pagePhase === 'round' || pagePhase === 'results');
    return () => setPresentation(false);
  }, [pagePhase, setPresentation]);

  useEffect(() => {
    if (pagePhase !== 'round') return;

    const loop = (ts: number) => {
      const dt = lastTsRef.current ? Math.min(ts - lastTsRef.current, 32) : 16;
      lastTsRef.current = ts;
      const { w, h } = stageSizeRef.current;

      if (playMode === 'exploracion') {
        const p = pitchRef.current;
        const result = tickExploreGame(
          exploreRef.current,
          dt,
          w,
          h,
          p.midi,
          p.pitchId,
          p.isSinging,
          ts
        );

        if (result.callSoundPitch) {
          playSingPitchSample(result.callSoundPitch);
        }
        if (result.collectedNoteIndex != null) {
          const item = result.state.items[result.collectedNoteIndex];
          if (item) playSingPitchSample(item.pitchId);
        }

        if (result.finished && result.state.phase === 'introDemo') {
          const countdown = beginExploreCountdown(result.state);
          exploreRef.current = countdown;
          setExploreGame(countdown);
        } else if (result.finished && result.state.phase === 'countdown') {
          const play = beginExplorePlay(result.state, w, h);
          exploreRef.current = play;
          setExploreGame(play);
        } else if (result.finished && result.state.phase === 'finished') {
          exploreRef.current = result.state;
          setExploreGame(result.state);
          stopRound();
          setPagePhase('results');
          return;
        } else {
          exploreRef.current = result.state;
          const alwaysRender =
            result.state.phase === 'introDemo' ||
            result.state.phase === 'countdown' ||
            result.visualDirty ||
            result.callSoundPitch != null ||
            result.collectedNoteIndex != null;
          if (alwaysRender) {
            setExploreGame(result.state);
          } else {
            stageRef.current?.syncExploreMotion(result.state);
          }
        }
      } else {
        const p = pitchRef.current;
        const result = tickClingGame(
          cantoRef.current,
          dt,
          w,
          w * 0.22,
          p.midi,
          p.pitchId,
          p.isSinging,
          h,
          ts
        );

        if (result.demoNoteIndex != null) {
          const note = result.state.exercise.notes[result.demoNoteIndex];
          if (note) playSingPitchSample(note.pitchId);
        }

        if (result.finished && result.state.phase === 'demo') {
          const countdown = beginCountdown(result.state);
          cantoRef.current = countdown;
          setCantoGame(countdown);
        } else if (result.state.phase === 'countdown' && result.finished) {
          const play = beginPlay(result.state, w);
          cantoRef.current = play;
          setCantoGame(play);
        } else if (result.state.phase === 'finished') {
          cantoRef.current = result.state;
          setCantoGame(result.state);
          stopRound();
          setPagePhase('results');
          return;
        } else {
          cantoRef.current = result.state;
          setCantoGame(result.state);
        }
      }

      animAccumRef.current += dt;
      if (animAccumRef.current >= CLING_SHEET.loopFrameMs) {
        animAccumRef.current %= CLING_SHEET.loopFrameMs;
        setAnimCycle((f) => f + 1);
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [pagePhase, playMode, stopRound]);

  useEffect(() => {
    if (pagePhase !== 'round' || !pitchRef.current.simulationMode) return;
    const onKey = (e: KeyboardEvent) => {
      const lanes = gamePitchLanes;
      const current = pitchRef.current.pitchId ?? lanes[Math.floor(lanes.length / 2)]!;
      const idx = lanes.indexOf(current);
      if (e.key === 'ArrowUp' && idx > 0) pitchApiRef.current.simulatePitch(lanes[idx - 1]!);
      else if (e.key === 'ArrowDown' && idx >= 0 && idx < lanes.length - 1)
        pitchApiRef.current.simulatePitch(lanes[idx + 1]!);
      else if (e.key === ' ') pitchApiRef.current.simulatePitch(current);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key.startsWith('Arrow')) pitchApiRef.current.simulatePitch(null);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [pagePhase, gamePitchLanes, pitch.simulationMode]);

  useEffect(() => {
    return () => {
      stopGameLoop();
      pitchApiRef.current.stopDetection();
    };
  }, [stopGameLoop]);

  return (
    <JuniorLayout immersive presentation={isPresentation}>
      <ActivityShell
        title={pagePhase === 'lobby' ? 'Menú de Configuración' : 'Aventura con Cling'}
        subtitle={pagePhase === 'lobby' ? 'Aventura con Cling' : subtitle}
        backTo="/melodia"
        backLabel="Melodía"
        immersive
        lockViewport
        presentation={isPresentation}
      >
        <div
          className={`flex flex-col flex-1 min-h-0 h-full overflow-hidden${
            isPresentation ? ' cling-round-root junior-presentation-active' : ''
          }`}
        >
        {pagePhase === 'lobby' && (
          <ClingCantaLobby
            mode={playMode}
            melody={exercise}
            exploreLevel={configuredExploreLevel}
            noteSelection={noteSelection}
            onTogglePitch={handleNoteToggle}
            onSelectSong={handleSelectSong}
            voiceTessitura={voiceTessitura}
            onVoiceTessitura={handleVoiceTessitura}
            characterId={characterId}
            onCharacter={handleCharacter}
            visual={visualSettings}
            onVisual={handleVisual}
            micReady={pitch.micReady}
            micError={pitch.micError}
            simulationMode={pitch.simulationMode}
            livePitch={pitch.pitchId}
            liveVolume={pitch.volume}
            liveMidi={pitch.midi}
            micActive={pitch.micActive}
            onRequestMic={() => void pitch.requestMic()}
            onStart={startRound}
          />
        )}

        {pagePhase === 'round' && (
          <ClingWorldStage
            ref={stageRef}
            mode={playMode}
            cantoGame={playMode === 'canto' ? cantoGame : undefined}
            exploreGame={playMode === 'exploracion' ? exploreGame : undefined}
            clingFrame={clingFlyFrame(animCycle)}
            clingFilter={getClingCharacter(characterId).filter}
            visual={visualSettings}
            isSinging={pitch.isSinging}
            volume={pitch.volume}
            micActive={pitch.micActive}
            detectedPitch={pitch.pitchId}
            onSizeChange={handleSizeChange}
          />
        )}

        {pagePhase === 'results' && (
          <ClingCantaResults
            mode={playMode}
            cantoGame={playMode === 'canto' ? cantoGame : undefined}
            exploreGame={playMode === 'exploracion' ? exploreGame : undefined}
            onRetry={handleRetry}
            onLobby={handleLobby}
          />
        )}
        </div>
      </ActivityShell>
    </JuniorLayout>
  );
}

export default function ClingCantaPage() {
  return (
    <RequireJuniorSession>
      <PresentationModeProvider>
        <ClingCantaPageInner />
      </PresentationModeProvider>
    </RequireJuniorSession>
  );
}
